import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import Iconify from 'src/components/iconify';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFSwitch, RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

const INTEGRATION_DISPLAY_NAME = 'ZEKI CRM';
const TOKEN_INTEGRATION_URL = 'https://crm.zekitec.com.br/integration/token';
const TOKEN_ONBOARDING_STEPS = [
  'Acesse a tela de Integração via API no ZEKI CRM.',
  'Clique em Novo, informe um nome para identificar o token e salve.',
  'Copie o token criado e cole no campo abaixo.',
];

export const GET_HELENA_STORE_INTEGRATION = gql`
  query GetHelenaStoreIntegration {
    getHelenaStoreIntegration {
      id
      apiTokenPreview
      connected
      activated
    }
  }
`;

const SAVE_HELENA_STORE_INTEGRATION = gql`
  mutation SaveHelenaStoreIntegration($input: HelenaStoreIntegrationInput!) {
    saveHelenaStoreIntegration(input: $input) {
      ok
      helenaStoreIntegration {
        id
        apiTokenPreview
        connected
        activated
      }
    }
  }
`;

export type HelenaIntegration = {
  id?: string | null;
  apiTokenPreview?: string | null;
  connected?: boolean | null;
  activated?: boolean | null;
};

type HelenaFormValues = {
  apiToken: string;
  activated: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  currentData?: HelenaIntegration | null;
  refetch: () => Promise<unknown>;
};

// ----------------------------------------------------------------------

export function ConfigHelenaModal({ open, onClose, currentData, refetch }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [saveIntegration, { loading: saving }] = useMutation(SAVE_HELENA_STORE_INTEGRATION);

  const methods = useForm<HelenaFormValues>({
    defaultValues: {
      apiToken: '',
      activated: true,
    },
  });

  const { handleSubmit, reset } = methods;

  const handleClose = () => {
    onClose();
    reset({
      apiToken: '',
      activated: currentData?.activated ?? true,
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    const token = data.apiToken.trim();
    if (!currentData && !token) {
      enqueueSnackbar('Informe o token de acesso para criar a integração.', { variant: 'error' });
      return;
    }

    const input: Record<string, unknown> = {
      activated: data.activated,
      connected: data.activated && Boolean(token || currentData?.apiTokenPreview),
    };

    if (token) {
      input.apiToken = token;
    }

    try {
      const response = await saveIntegration({ variables: { input } });
      if (!response.data?.saveHelenaStoreIntegration?.ok) {
        enqueueSnackbar('Não foi possível salvar a integração.', { variant: 'error' });
        return;
      }

      enqueueSnackbar('Integração salva com sucesso!', { variant: 'success' });
      await refetch();
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Não foi possível salvar a integração.', {
        variant: 'error',
      });
    }
  });

  useEffect(() => {
    if (!open) return;

    reset({
      apiToken: '',
      activated: currentData?.activated ?? true,
    });
  }, [currentData, open, reset]);

  return (
    <FormProvider methods={methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 560 },
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          fontSize={20}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Iconify icon="solar:chat-round-like-bold" width={24} sx={{ color: 'primary.main' }} />
          Integração {INTEGRATION_DISPLAY_NAME}
        </DialogTitle>

        <DialogContent
          sx={{
            width: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            py: 2,
          }}
        >
          <Stack spacing={1.5}>
            <Alert severity="info" variant="outlined">
              Gere um token permanente no ZEKI CRM para liberar a listagem de canais e templates.
              O canal de envio será escolhido depois, no fluxo de disparo.
            </Alert>

            <Stack spacing={1.25}>
              <Button
                fullWidth
                href={TOKEN_INTEGRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                endIcon={<Iconify icon="solar:arrow-right-up-linear" />}
                sx={{ justifyContent: 'space-between' }}
              >
                Abrir tela de tokens do ZEKI CRM
              </Button>

              <Stack spacing={1}>
                {TOKEN_ONBOARDING_STEPS.map((step, index) => (
                  <Stack key={step} direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        flexShrink: 0,
                        borderRadius: '50%',
                        typography: 'caption',
                        fontWeight: 700,
                        color: 'primary.main',
                        bgcolor: 'primary.lighter',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>

          {currentData?.apiTokenPreview && (
            <Typography variant="caption" color="text.secondary">
              Token salvo: {currentData.apiTokenPreview}. Informe um novo token somente se quiser
              trocar as credenciais.
            </Typography>
          )}

          <Stack spacing={1.5}>
            <RHFTextField
              name="apiToken"
              label="Token de acesso"
              type="password"
              placeholder={currentData ? 'Manter token atual' : 'Cole o token de acesso'}
              helperText="Cole apenas o token gerado, sem o prefixo Bearer."
            />
          </Stack>

          <RHFSwitch name="activated" label="Ativar integração" />
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Fechar
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={onSubmit}>
            Salvar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
